// ============ Date/Time Formatters ============
export const fmtDate = (d?: string | Date | null): string => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return '—'; }
};

export const fmtDateTime = (d?: string | Date | null): string => {
  if (!d) return '—';
  try { return new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return '—'; }
};

export const fmtTime = (t?: string | null): string => {
  if (!t) return '—';
  try {
    const [h, m] = t.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`;
  } catch { return t; }
};

// ============ Currency ============
export const fmtCurrency = (amount: number | string | null | undefined): string => {
  const n = parseFloat(String(amount || 0));
  if (isNaN(n)) return '₹0';
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000)   return `₹${(n / 100000).toFixed(2)} L`;
  if (n >= 1000)     return `₹${(n / 1000).toFixed(1)}K`;
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
};

// ============ Status Colors ============
export const statusColor = (status: string): string => {
  const map: Record<string, string> = {
    ACTIVE: 'bg-green-100 text-green-700', INACTIVE: 'bg-gray-100 text-gray-600',
    PENDING: 'bg-yellow-100 text-yellow-700', COMPLETED: 'bg-green-100 text-green-700',
    CANCELLED: 'bg-red-100 text-red-700', CONFIRMED: 'bg-blue-100 text-blue-700',
    SCHEDULED: 'bg-blue-100 text-blue-700', IN_PROGRESS: 'bg-purple-100 text-purple-700',
    CHECKED_IN: 'bg-teal-100 text-teal-700', IN_CONSULTATION: 'bg-amber-100 text-amber-700',
    NO_SHOW: 'bg-slate-200 text-slate-600', BOOKED: 'bg-blue-100 text-blue-700',
    PAID: 'bg-green-100 text-green-700', UNPAID: 'bg-red-100 text-red-700',
    PARTIAL: 'bg-amber-100 text-amber-700', ADMITTED: 'bg-blue-100 text-blue-700',
    DISCHARGED: 'bg-gray-100 text-gray-600', OPERATIONAL: 'bg-green-100 text-green-700',
  };
  return map[status?.toUpperCase()] || 'bg-gray-100 text-gray-600';
};

// ============ Age Calculator ============
export const calcAge = (dob?: string | null): string => {
  if (!dob) return '—';
  try {
    const diff = Date.now() - new Date(dob).getTime();
    const years = Math.floor(diff / (365.25 * 24 * 3600 * 1000));
    return `${years} yrs`;
  } catch { return '—'; }
};

// ============ Generators ============
export const generateUHID = (): string => {
  const now = new Date();
  const year = now.getFullYear().toString().slice(2);
  const rand = Math.floor(10000 + Math.random() * 90000);
  return `UH${year}${rand}`;
};

// ============ Safe Array Extractor ============
/** Safely extract an array from an API response that might be:
 *  - a raw array
 *  - { data: [...] }
 *  - { data: { patients: [...] } } (pass path: ['patients'])
 *  - null/undefined
 */
export const safeArray = (data: any, path?: string[]): any[] => {
  if (Array.isArray(data)) return data;
  if (!data) return [];
  let cur = data;
  if (path) {
    for (const key of path) {
      if (cur && typeof cur === 'object' && key in cur) cur = cur[key];
      else return [];
    }
    return Array.isArray(cur) ? cur : [];
  }
  for (const key of ['data', 'items', 'rows', 'results']) {
    if (Array.isArray(cur?.[key])) return cur[key];
  }
  return [];
};

// ============ String Utilities ============
export const truncate = (str: string, len = 50): string =>
  str && str.length > len ? str.slice(0, len) + '...' : str || '';

export const safeJSON = (val: any): string => {
  try { return JSON.stringify(val, null, 2); }
  catch { return String(val); }
};

// ============ Constants ============
export const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-'] as const;
export const GENDERS = ['MALE', 'FEMALE', 'OTHER'] as const;
export const PAYMENT_MODES = ['CASH', 'CARD', 'UPI', 'NET_BANKING', 'CHEQUE', 'INSURANCE', 'CREDIT'] as const;
export const CONSULT_TYPES = ['NEW', 'FOLLOW_UP', 'EMERGENCY', 'REFERRAL', 'TELECONSULT'] as const;
export const PRIORITIES = ['ROUTINE', 'URGENT', 'STAT', 'EMERGENCY'] as const;
export const INDIAN_STATES = [
  'Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Goa','Gujarat',
  'Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh',
  'Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland','Odisha','Punjab',
  'Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura','Uttar Pradesh',
  'Uttarakhand','West Bengal','Delhi','Jammu & Kashmir','Ladakh','Puducherry',
] as const;
